import type React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { tierApi } from "../api/apiRequests";
import { useTierDetails } from "../api/swr/useTierDetails";
import { useTiers } from "../api/swr/useTiers";
import {
  TitleAndDescriptionForm,
  type FormValues,
} from "../molecules/TitleAndDescriptionForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { useState } from "react";

interface Props {
  tierId?: number;
  trigger: React.ReactNode;
}

export const TierModal = ({ tierId, trigger }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("commons.document.tier")}</DialogTitle>
          {tierId ? (
            <EditTier tierId={tierId} onSuccess={() => setOpen(false)} />
          ) : (
            <CreateTier onSuccess={() => setOpen(false)} />
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};

interface EditTierProps {
  tierId: number;
  onSuccess: () => void;
}

const EditTier = ({ tierId, onSuccess }: EditTierProps) => {
  const { t } = useTranslation();
  const { data, mutate } = useTierDetails(tierId, true);
  const { mutate: mutateAll } = useTiers();

  const removeHandler = async () => {
    try {
      await tierApi.delete(tierId);
      void mutateAll();
      toast.success(t("toasts.success.tierRemoved"));
    } catch {
      toast.error(t("toasts.error.tierNotRemoved"));
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      await tierApi.update(values, tierId);
      void mutate();
      void mutateAll();
      toast.success(t("toasts.success.tierUpdated"));
      onSuccess();
    } catch {
      toast.error(t("toasts.error.generic"));
    }
  };

  return (
    <TitleAndDescriptionForm
      data={data}
      onDelete={removeHandler}
      onSubmit={onSubmit}
    />
  );
};

interface CreateTierProps {
  onSuccess: () => void;
}

const CreateTier = ({ onSuccess }: CreateTierProps) => {
  const { t } = useTranslation();
  const { data: tiers, mutate: mutateAllTiers } = useTiers();

  const lastTier = tiers?.at(-1);

  const onSubmit = async (values: FormValues) => {
    try {
      await tierApi.create({
        ...values,
        parentId: lastTier?.id ?? null,
      });
      void mutateAllTiers();
      toast.success(t("toasts.success.tierCreated"));
      onSuccess();
    } catch {
      toast.error(t("toasts.error.tierNotCreated"));
    }
  };

  return <TitleAndDescriptionForm onSubmit={onSubmit} />;
};
